// API calls for booking notes (GET/POST api/ams/booking-requests/{id}/notes, DELETE .../notes/{noteId}), plus the small pure
// helpers the notes section and the cancel form share.
import { api } from "@/services/api"
import { type BookingNote, BookingNoteAction, BookingNoteVisibility } from "../bookingCalendarApi"
import { SITE_TIME_ZONE } from "../lib/siteTime"

/** Matches the server's floor on a staff cancellation's internal note — see StaffCancelBookingRequest. */
export const MIN_INTERNAL_NOTE_LENGTH = 3

/** Matches the server's cap on either cancellation note — see StaffCancelBookingRequest. */
export const MAX_CANCEL_NOTE_LENGTH = 500

/** Matches the column's cap on a general note — see AmsBookingNoteService.MaxBodyLength. */
export const MAX_GENERAL_NOTE_LENGTH = 2000

// AmsBookings too: adding or deleting a note changes the count the calendar's cells are marked with.
const taggedApi = api.enhanceEndpoints({ addTagTypes: ["AmsBookingNotes", "AmsBookings"] })

export const notesApi = taggedApi.injectEndpoints({
  endpoints: (builder) => ({
    /** Every note on the booking, oldest first. Internal ones included: this is a staff-only endpoint. */
    getBookingNotes: builder.query<BookingNote[], string>({
      query: (requestId) => ({ url: `/api/ams/booking-requests/${encodeURIComponent(requestId)}/notes` }),
      providesTags: (_result, _error, requestId) => [{ type: "AmsBookingNotes" as const, id: requestId }],
    }),
    /** Adds a general internal note, written by whoever is signed in. */
    addBookingNote: builder.mutation<BookingNote, { requestId: string; body: string }>({
      query: ({ requestId, body }) => ({
        url: `/api/ams/booking-requests/${encodeURIComponent(requestId)}/notes`,
        method: "POST",
        body: { body },
      }),
      invalidatesTags: (_result, _error, { requestId }) => [{ type: "AmsBookingNotes" as const, id: requestId }, "AmsBookings"],
    }),
    /** Deletes a general internal note. The server keeps every other kind and answers 409. */
    deleteBookingNote: builder.mutation<void, { requestId: string; noteId: string }>({
      query: ({ requestId, noteId }) => ({
        url: `/api/ams/booking-requests/${encodeURIComponent(requestId)}/notes/${encodeURIComponent(noteId)}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { requestId }) => [{ type: "AmsBookingNotes" as const, id: requestId }, "AmsBookings"],
    }),
  }),
})

export const { useGetBookingNotesQuery, useAddBookingNoteMutation, useDeleteBookingNoteMutation } = notesApi

/** Whether a note is staff-only. Anything the server does not mark external is treated as internal. */
export function isInternalNote(note: Pick<BookingNote, "visibility">): boolean {
  return note.visibility !== BookingNoteVisibility.External
}

const ACTION_LABELS: Record<number, string> = {
  [BookingNoteAction.General]: "Note",
  [BookingNoteAction.Cancellation]: "Cancellation",
  [BookingNoteAction.Reschedule]: "Reschedule",
  [BookingNoteAction.StatusChange]: "Status change",
  [BookingNoteAction.ReviewDecision]: "Review decision",
  [BookingNoteAction.RescheduleLink]: "Reschedule link",
  [BookingNoteAction.System]: "System",
}

/**
 * Whether staff may delete a note: only a general internal one, the kind added by hand. Every other
 * note records something done to the booking or something the customer was told. Mirrors
 * AmsBookingNoteService.IsDeletable.
 */
export function isDeletableNote(note: Pick<BookingNote, "visibility" | "action">): boolean {
  return isInternalNote(note) && note.action === BookingNoteAction.General
}

/** Who is looking at the notes, as far as deleting them goes. */
export interface NoteViewer {
  /** Their UPN, which is what a note they wrote carries in `createdBy`. Null when unknown. */
  upn: string | null
  /** Whether they hold appointments:manage, which may delete anybody's note. */
  canDeleteAny: boolean
}

/**
 * Whether this viewer may delete the note: a deletable kind, and either their own or they manage
 * appointments. Mirrors the server's check in AmsBookingNoteService.DeleteAsync.
 */
export function mayDeleteNote(note: Pick<BookingNote, "visibility" | "action" | "createdBy">, viewer: NoteViewer): boolean {
  if (!isDeletableNote(note)) return false
  if (viewer.canDeleteAny) return true
  return viewer.upn != null && note.createdBy.trim().toLowerCase() === viewer.upn.trim().toLowerCase()
}

/** What the booking was going through when the note was written. */
export function noteActionLabel(action: number): string {
  return ACTION_LABELS[action] ?? "Note"
}

const noteTimeFormat = new Intl.DateTimeFormat("en-US", {
  timeZone: SITE_TIME_ZONE,
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  timeZoneName: "short",
})

/** When a note was written, in the sites' own time zone (Pacific), whatever the reader's machine says. */
export function formatNoteTime(iso: string): string {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : noteTimeFormat.format(date)
}

/** What is wrong with the cancel form's two notes, or null when it can be sent. */
export function cancelNotesProblem(internalNote: string, externalNote: string): string | null {
  const internal = internalNote.trim()
  if (internal.length < MIN_INTERNAL_NOTE_LENGTH) {
    return `The internal note needs at least ${MIN_INTERNAL_NOTE_LENGTH} characters.`
  }
  if (internalNote.length > MAX_CANCEL_NOTE_LENGTH) {
    return `The internal note is at most ${MAX_CANCEL_NOTE_LENGTH} characters.`
  }
  if (externalNote.length > MAX_CANCEL_NOTE_LENGTH) {
    return `The note for the customer is at most ${MAX_CANCEL_NOTE_LENGTH} characters.`
  }
  return null
}
